import type { Aabb2, Point2 } from '../geometry/point';

/**
 * Which items a marquee collects.
 *
 * Follows which side of its anchor the pointer is on, live: dragging back across
 * the anchor flips the box's meaning, the way it does in every CAD package. It is
 * NOT latched at the first horizontal movement — doing that let a stray pixel of
 * jitter at the very start decide the whole gesture, which is indistinguishable
 * from random to the person dragging.
 */
export type MarqueeRule =
  /** Left to right: only items entirely inside the box. */
  | 'enclosed'
  /** Right to left: anything the box touches, except whatever contains it. */
  | 'crossing';

/** What the release does to the existing selection. */
export type MarqueeMergeMode = 'replace' | 'add' | 'subtract';

export type MarqueeState = {
  /** Latest pointer position. */
  readonly pointer: Point2;
  /**
   * Corner the box grows from, and the reference the rule is measured against.
   *
   * Starts at the press point. Moves when Space translates the box, and is
   * recomputed when mirroring ends so the box does not jump — which is exactly
   * why the rule is measured from here rather than from where the press landed:
   * both operations carry the anchor with them, so neither can reinterpret the
   * gesture's direction, and the spec's "stays fixed if moved or mirrored" falls
   * out of the geometry instead of needing a latch.
   */
  readonly anchor: Point2;
  /** Centre the box mirrors about, or `null` when corner-anchored. */
  readonly mirrorCenter: Point2 | null;
  /**
   * `null` until the drag has moved horizontally at all, then the live direction —
   * except while mirroring, where it holds its last corner-anchored value because
   * a box growing symmetrically has no direction to speak of.
   */
  readonly rule: MarqueeRule | null;
  /** Merge mode implied by the modifiers at the press. Alt overrides it live. */
  readonly baseMode: Exclude<MarqueeMergeMode, 'subtract'>;
  readonly isAltHeld: boolean;
  /** Alt was held before the press, which suppresses mirroring. */
  readonly didStartWithAlt: boolean;
  /** Alt has been let go at least once, which re-enables mirroring. */
  readonly hasReleasedAlt: boolean;
  readonly isSpaceHeld: boolean;
  /** Escape was pressed: the box is gone and the release does nothing. */
  readonly isAborted: boolean;
};

const rectCenter = ([minX, minY, maxX, maxY]: Aabb2): Point2 => [
  (minX + maxX) / 2,
  (minY + maxY) / 2,
];

export const beginMarquee = (
  point: Point2,
  { isShiftHeld = false, isAltHeld = false } = {},
): MarqueeState => ({
  pointer: point,
  anchor: point,
  mirrorCenter: null,
  rule: null,
  baseMode: isShiftHeld ? 'add' : 'replace',
  isAltHeld,
  didStartWithAlt: isAltHeld,
  hasReleasedAlt: false,
  isSpaceHeld: false,
  isAborted: false,
});

/** The box, in the same screen space the points were given in. */
export const marqueeRect = (state: MarqueeState): Aabb2 => {
  const [pointerX, pointerY] = state.pointer;

  if (state.mirrorCenter) {
    const [centerX, centerY] = state.mirrorCenter;
    const halfWidth = Math.abs(pointerX - centerX);
    const halfHeight = Math.abs(pointerY - centerY);
    return [
      centerX - halfWidth,
      centerY - halfHeight,
      centerX + halfWidth,
      centerY + halfHeight,
    ];
  }

  const [anchorX, anchorY] = state.anchor;
  return [
    Math.min(anchorX, pointerX),
    Math.min(anchorY, pointerY),
    Math.max(anchorX, pointerX),
    Math.max(anchorY, pointerY),
  ];
};

/**
 * Merge mode as it stands right now.
 *
 * Derived rather than stored, so that letting Alt go returns the gesture to
 * whatever the press asked for instead of stranding it in subtract.
 */
export const marqueeMergeMode = (state: MarqueeState): MarqueeMergeMode =>
  state.isAltHeld ? 'subtract' : state.baseMode;

/**
 * Which side of the anchor the pointer is on, or `null` when it is level with it.
 *
 * Level means the box has no horizontal extent, so there is no direction to read
 * and the previous answer stands.
 */
const directionRule = (pointer: Point2, anchor: Point2): MarqueeRule | null => {
  if (pointer[0] === anchor[0]) {
    return null;
  }
  return pointer[0] > anchor[0] ? 'enclosed' : 'crossing';
};

/** Border treatment. Solid encloses, dashed crosses — the AutoCAD convention. */
export const marqueeBorderStyle = (state: MarqueeState): 'solid' | 'dashed' =>
  state.rule === 'crossing' ? 'dashed' : 'solid';

/** True when the box has any area to collect with. */
export const isMarqueeLive = (state: MarqueeState): boolean => {
  const [minX, minY, maxX, maxY] = marqueeRect(state);
  return !state.isAborted && (maxX > minX || maxY > minY);
};

/**
 * Advances the drag to a new pointer position.
 *
 * With Space held the box keeps its size and rides along with the pointer; the
 * anchor (and the mirror centre, if any) move with it, which is what lets a later
 * Space release resume resizing from where the box now sits rather than snapping
 * back to where the drag began.
 */
export const moveMarquee = (
  state: MarqueeState,
  point: Point2,
): MarqueeState => {
  if (state.isAborted) {
    return state;
  }

  if (state.isSpaceHeld) {
    const deltaX = point[0] - state.pointer[0];
    const deltaY = point[1] - state.pointer[1];
    return {
      ...state,
      pointer: point,
      anchor: [state.anchor[0] + deltaX, state.anchor[1] + deltaY],
      mirrorCenter: state.mirrorCenter
        ? [state.mirrorCenter[0] + deltaX, state.mirrorCenter[1] + deltaY]
        : null,
    };
  }

  // Mirroring grows the box both ways at once, so it has no direction to read —
  // the last corner-anchored answer stands until corner anchoring resumes.
  const rule = state.mirrorCenter
    ? state.rule
    : directionRule(point, state.anchor) ?? state.rule;

  return { ...state, pointer: point, rule };
};

/**
 * Presses or releases Alt mid-drag.
 *
 * Engaging Alt on a box that already exists switches to mirroring about the box's
 * own centre — and because the pointer sits on a corner at that moment, the centre
 * is exactly half a diagonal away, so the box is unchanged by the switch. Letting
 * Alt go re-derives the opposite corner from that same centre, so it is unchanged
 * again and corner anchoring resumes without a jump.
 *
 * Alt held from before the press only subtracts. Mirroring stays off until Alt has
 * been released once, which is the spec's way of separating "I meant to subtract"
 * from "I want to resize about the centre".
 */
export const setMarqueeAlt = (
  state: MarqueeState,
  isAltHeld: boolean,
): MarqueeState => {
  if (state.isAborted || state.isAltHeld === isAltHeld) {
    return state;
  }

  if (isAltHeld) {
    const canMirror = !state.didStartWithAlt || state.hasReleasedAlt;
    return {
      ...state,
      isAltHeld: true,
      mirrorCenter: canMirror ? rectCenter(marqueeRect(state)) : null,
    };
  }

  if (!state.mirrorCenter) {
    return { ...state, isAltHeld: false, hasReleasedAlt: true };
  }

  // Opposite corner = centre reflected through the pointer.
  const anchor: Point2 = [
    2 * state.mirrorCenter[0] - state.pointer[0],
    2 * state.mirrorCenter[1] - state.pointer[1],
  ];
  return {
    ...state,
    isAltHeld: false,
    hasReleasedAlt: true,
    mirrorCenter: null,
    anchor,
  };
};

export const setMarqueeSpace = (
  state: MarqueeState,
  isSpaceHeld: boolean,
): MarqueeState =>
  state.isAborted || state.isSpaceHeld === isSpaceHeld
    ? state
    : { ...state, isSpaceHeld };

/** Escape: the box vanishes and the release does nothing at all. */
export const abortMarquee = (state: MarqueeState): MarqueeState =>
  state.isAborted ? state : { ...state, isAborted: true };
